import React, { useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, xcss } from '@atlaskit/primitives';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AutomationIcon from '@atlaskit/icon/core/automation';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';

import type { EventSourceName } from './utils/analytics';
import { markDiscoveryExperienceAsSeen } from './utils/localStorage';
import { InContextAutomationPrompt } from './InContextAutomationPrompt';
import { meetingNotesI18n, commonI18n } from './i18n';

const insightMessageStyles = xcss({ marginLeft: 'space.300' });
const i18n = defineMessages({
	successIconLabel: {
		id: 'automation-discovery.global-public-link.success.icon.label',
		defaultMessage: 'Success check icon',
		description: 'Accessibility label to describe a green success check icon',
	},
	modalCloseIconLabel: {
		id: 'automation-discovery.global-public-link.modal.close.icon.label',
		defaultMessage: 'Close icon for modal',
		description: 'Accessibility label to describe a cross icon to close the modal',
	},
});

type BaseAutomationDiscoveryDialogProps = {
	onClose: () => void;
	// TODO: replace with templateId
	templateUrl: string;
	templateId?: string;
	templateName: string;
	discoveryExperience: string;
	spaceKey?: string | null;
	header: React.ReactNode;
	cta: React.ReactNode;
	eventSourceName: EventSourceName;
	localStorageKey: string;
	customBody?: React.ReactNode;
	onDismiss?: () => void;
	onRedirectToRuleBuilder?: () => void;
	onCreateRuleSuccess?: () => void;
	onCreateRuleFail?: (error: ApolloErrorInterop) => void;
	onRuleCreateStart?: () => void;
	disableCreateRule?: boolean;
	testId?: string;
	insightMessage?: React.ReactNode;
	children?: React.ReactNode;
};

const BaseAutomationDiscoveryDialogComponent: React.FC<BaseAutomationDiscoveryDialogProps> = ({
	onClose,
	templateUrl,
	templateName,
	spaceKey,
	header,
	cta,
	eventSourceName,
	localStorageKey,
	discoveryExperience,
	customBody,
	children,
	templateId = '',
	onDismiss = () => {},
	onRedirectToRuleBuilder = () => {},
	onCreateRuleSuccess = () => {},
	onRuleCreateStart,
	onCreateRuleFail,
	disableCreateRule,
	testId,
	insightMessage,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: discoveryExperience,
				source: eventSourceName,
			},
		}).fire();
	}, [createAnalyticsEvent, eventSourceName, discoveryExperience]);

	const handleDismiss = () => {
		markDiscoveryExperienceAsSeen(localStorageKey);
		onClose();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${discoveryExperience}Dismiss`,
				source: eventSourceName,
				attributes: {
					spaceKey,
				},
			},
		}).fire();
	};

	const handleProceed = () => {
		window.open(templateUrl, '_blank');

		markDiscoveryExperienceAsSeen(localStorageKey);
		onClose();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: `${discoveryExperience}Proceed`,
				source: eventSourceName,
				attributes: {
					spaceKey,
					template: templateName,
				},
			},
		}).fire();
	};

	return (
		<Modal width="small" onClose={onClose} testId={testId} messageType="transactional">
			<Flex
				justifyContent="end"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				xcss={xcss({
					padding: 'space.100',
					paddingRight: 'space.200',
				})}
			>
				<Button
					aria-label={formatMessage(meetingNotesI18n.modalCloseIconLabel)}
					appearance="link"
					onClick={onClose}
					iconBefore={<CrossIcon label="" color={token('color.text.subtle')} />}
				/>
			</Flex>
			<ModalBody>
				<Inline space="space.100">
					<StatusSuccessIcon
						label={formatMessage(i18n.successIconLabel)}
						color={token('color.icon.success')}
						spacing="spacious"
					/>
					<Heading size="small">{header}</Heading>
				</Inline>
				{customBody}
			</ModalBody>
			<ModalFooter>
				{children ? (
					<InContextAutomationPrompt
						ruleComponentAlignment="stack"
						templateId={templateId}
						templateDescription={templateName}
						spaceKey={spaceKey}
						message={cta}
						insightMessage={insightMessage}
						handleRedirectToRuleBuilder={onRedirectToRuleBuilder}
						handleDismiss={onDismiss}
						handleRuleCreationSuccess={onCreateRuleSuccess}
						handleRuleCreationStart={onRuleCreateStart}
						handleRuleCreationFail={onCreateRuleFail}
						checkIsInContextRuleBuilderStateValid={() => true}
						automationDiscoveryParent={eventSourceName}
						displayMode="modal"
						disableCreateRule={disableCreateRule}
					>
						{children}
					</InContextAutomationPrompt>
				) : (
					<Stack
						grow="fill"
						space="space.150"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
						xcss={xcss({
							padding: 'space.150',
							backgroundColor: 'color.background.accent.gray.subtlest',
						})}
					>
						<Inline alignBlock="center" space="space.100">
							<AutomationIcon label="" color={token('color.icon.accent.blue')} />
							<Heading size="xsmall">{cta}</Heading>
						</Inline>
						{insightMessage && <Box xcss={insightMessageStyles}>{insightMessage}</Box>}
						<Inline alignBlock="center" space="space.200" alignInline="end">
							<Button appearance="link" onClick={handleDismiss} spacing="none">
								<FormattedMessage {...commonI18n.dismiss} />
							</Button>
							<Button appearance="link" onClick={handleProceed} spacing="none">
								<FormattedMessage {...commonI18n.showMeHow} />
							</Button>
						</Inline>
					</Stack>
				)}
			</ModalFooter>
		</Modal>
	);
};

export const BaseAutomationDiscoveryDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(BaseAutomationDiscoveryDialogComponent);
